import { useId, useRef, useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

export type DiagonalLinesCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

export interface DiagonalLinesProps {
  /**
   * Angle of the lines from horizontal, in degrees.
   * Defaults to 24 — the brand trademark angle.
   */
  angle?: number
  /**
   * Perpendicular distance between line centres, in pixels.
   * Defaults to 12.
   */
  spacing?: number
  /**
   * Exact number of lines to render. When set, renders individual lines at the
   * given `spacing` instead of an infinite repeating pattern — so spacing is
   * preserved and exactly this many lines are drawn.
   */
  lineCount?: number
  /** Line stroke width in pixels. Defaults to 1. */
  strokeWidth?: number
  /**
   * Line color — accepts any CSS color value or 'currentColor'.
   * Defaults to 'currentColor', inheriting the nearest text color.
   */
  color?: string
  /**
   * Overall opacity of the pattern (0–1). Useful for overlay use-cases.
   * Defaults to 1.
   */
  opacity?: number
  /**
   * Which corner of the container the lines radiate from / the rotation anchors to.
   * Defaults to 'top-left'.
   */
  corner?: DiagonalLinesCorner
  /** Extra Tailwind / CSS classes applied to the root <svg>. */
  className?: string
}

/**
 * DiagonalLines
 *
 * Renders the brand-trademark repeating diagonal line pattern as a scalable
 * SVG that fills 100 % of its container.
 *
 * - Use `spacing` to control the gap between lines.
 * - Use `lineCount` to show an exact number of lines (spacing is preserved).
 * - Use `corner` to choose which corner the lines radiate from.
 *
 * **Masking / overlay usage** — make the parent `relative overflow-hidden`
 * and add `absolute inset-0` to this component via `className`:
 *
 * ```tsx
 * <div className="relative overflow-hidden rounded-xl h-48 bg-brand-orange">
 *   <DiagonalLines
 *     className="absolute inset-0 opacity-20"
 *     color="white"
 *     lineCount={8}
 *     strokeWidth={2}
 *     corner="top-right"
 *   />
 *   <p className="relative z-10">Content on top</p>
 * </div>
 * ```
 */
export function DiagonalLines({
  angle = 24,
  spacing = 12,
  lineCount,
  strokeWidth = 1,
  color = 'currentColor',
  opacity = 1,
  corner = 'top-left',
  className,
}: DiagonalLinesProps) {
  const uid = useId()
  // useId returns strings like ":r0:" — strip colons, which are invalid in SVG ids
  const patternId = `dl-${uid.replace(/:/g, '')}`

  const svgRef = useRef<SVGSVGElement>(null)
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null)

  useEffect(() => {
    const el = svgRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0]!.contentRect
      setDims({ w: width, h: height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Rotation anchor point in SVG user-space coordinates
  const anchorX = corner === 'top-right' || corner === 'bottom-right' ? (dims?.w ?? 0) : 0
  const anchorY = corner === 'bottom-left' || corner === 'bottom-right' ? (dims?.h ?? 0) : 0

  // Keep the angle relative to the anchor corner so all four corners are mirror images.
  // Top corners flip along the horizontal axis (negate), right corners flip along the vertical.
  // Combined: TL=-angle, TR=+angle, BL=+angle, BR=-angle.
  const effectiveAngle = (corner === 'top-left' || corner === 'bottom-right') ? -angle : angle

  // ── Individual line mode ────────────────────────────────────────────────────
  // When lineCount is set: render exactly N lines at the given spacing.
  // Lines spread away from the anchor corner (top → down, bottom → up).
  if (lineCount != null) {
    const diagonal = dims ? Math.sqrt(dims.w ** 2 + dims.h ** 2) : 0
    const lineHalf = diagonal * 1.5 // long enough to cross the container at any angle
    // Top corners spread lines downward (+y), bottom corners spread upward (−y)
    const dir = corner === 'bottom-left' || corner === 'bottom-right' ? -1 : 1

    return (
      <svg
        ref={svgRef}
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        className={cn(className)}
        style={{ opacity }}
      >
        {dims && (
          <g transform={`rotate(${effectiveAngle}, ${anchorX}, ${anchorY})`}>
            {Array.from({ length: lineCount }, (_, i) => (
              <line
                key={i}
                x1={anchorX - lineHalf}
                y1={anchorY + i * spacing * dir}
                x2={anchorX + lineHalf}
                y2={anchorY + i * spacing * dir}
                stroke={color}
                strokeWidth={strokeWidth}
              />
            ))}
          </g>
        )}
      </svg>
    )
  }

  // ── Pattern mode (infinite tiling) ─────────────────────────────────────────
  return (
    <svg
      ref={svgRef}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      className={cn(className)}
      style={{ opacity }}
    >
      <defs>
        <pattern
          id={patternId}
          patternUnits="userSpaceOnUse"
          width={spacing}
          height={spacing}
          patternTransform={`rotate(${effectiveAngle}, ${anchorX}, ${anchorY})`}
        >
          {/* Horizontal line in pattern space; patternTransform rotates it to the desired angle */}
          <line
            x1="0"
            y1="0"
            x2={spacing}
            y2="0"
            stroke={color}
            strokeWidth={strokeWidth}
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  )
}
